import React, { useEffect, useRef, useState } from 'react';
import { deleteOwnAvatar, setAdminAvatarCache, uploadOwnAvatar, useAdminAvatar } from '../../lib/avatars';
import { useTranslation } from 'react-i18next';
import { Badge, Card, Button, Field, Input } from '../../components/ui';
import { BRAND, getThemeColors, DENSITY_VALUES } from '../../lib/theme';
import { useIsMobile } from '../../lib/useBreakpoint';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { v1Admins, type V1Admin } from '../../lib/v1';
import { v2ThreeCx } from '../../lib/threeCx';
import type { Density } from '../../types';

interface ProfilPageProps {
  dark: boolean;
  density: Density;
  /** Optional jump-to handler for the security card's "2FA verwalten" button. */
  setPage?: (p: string) => void;
}

interface ProfileForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
}

/** Compute "AS" / "DT" style initials from a name (fallback: email-local-part, then "??"). */
function initialsFor(name: string, email: string): string {
  const src = (name && name.trim()) || (email ? email.split('@')[0] : '');
  if (!src) return '??';
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

/** Map v1 numeric admin.role to a translated label (admin.role is a small int taxonomy). */
function roleLabel(role: number | undefined | null, tr: (key: string, opts?: Record<string, unknown>) => string): string {
  switch (role) {
    case 1: return tr('profil.role.super_admin');
    case 2: return tr('profil.role.admin');
    case 3: return tr('profil.role.employee');
    case 4: return tr('profil.role.accounting');
    case 5: return tr('profil.role.readonly');
    default: return role == null ? tr('profil.role.admin') : tr('profil.role.generic', { n: role });
  }
}

export function ProfilPage({ dark, density, setPage }: ProfilPageProps) {
  const t = getThemeColors(dark);
  const { t: tr, i18n } = useTranslation();
  const locale = i18n.language || 'de';
  const d = DENSITY_VALUES[density];
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { threeCxExtension, refresh: refreshPermissions } = usePermissions();

  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [admin, setAdmin]     = useState<V1Admin | null>(null);
  const [form, setForm] = useState<ProfileForm>({
    firstName: '', lastName: '', email: '', phone: '', role: '',
  });

  // CRM 2.0 / feature/crm2-3cx-integration — Telefonie-Extension.
  // Wird zusammen mit "Profil bearbeiten" editiert/gespeichert (kein
  // eigener Button mehr).
  const [extensionValue, setExtensionValue]     = useState('');
  const [extensionError, setExtensionError]     = useState<string | null>(null);

  useEffect(() => {
    setExtensionValue(threeCxExtension ?? '');
  }, [threeCxExtension]);

  // Initial load: pull the full admin record so we have vorname/nachname/mobile.
  // The login response only carries id/name/email/role.
  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    (async () => {
      try {
        const a = await v1Admins.get(user.id);
        if (!alive) return;
        setAdmin(a);
        setForm({
          firstName: a.vorname ?? '',
          lastName:  a.nachname ?? '',
          email:     a.email,
          phone:     a.mobile ?? '',
          role:      roleLabel(a.role, tr),
        });
      } catch (e) {
        if (!alive) return;
        setError(e instanceof Error ? e.message : tr('profil.error.load'));
      }
    })();
    return () => { alive = false; };
  }, [user?.id]);

  const setField = (k: keyof ProfileForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const handleEditOrSave = async () => {
    if (!admin) return;
    if (!editing) {
      setEditing(true);
      return;
    }
    // SAVE
    setSaving(true);
    setError(null);
    setExtensionError(null);
    try {
      const updated = await v1Admins.update(admin.id, {
        konto:    admin.konto,           // validator requires these even on partial edit
        role:     admin.role,
        email:    form.email.trim(),
        vorname:  form.firstName.trim(),
        nachname: form.lastName.trim(),
        mobile:   form.phone.trim() || null,
      });
      // 3CX-Extension im selben Speichern-Flow mitschreiben.
      if ((extensionValue.trim() || null) !== (threeCxExtension ?? null)) {
        try {
          await v2ThreeCx.updateExtension(extensionValue.trim() || null);
          refreshPermissions();
        } catch (e) {
          setExtensionError(e instanceof Error ? e.message : tr('profil.error.save'));
        }
      }
      setAdmin(updated);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : tr('profil.error.save'));
    } finally {
      setSaving(false);
    }
  };

  const avatarUrl = useAdminAvatar(admin?.id ?? null);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const avatarInput = useRef<HTMLInputElement>(null);
  useEffect(() => { setAvatarFailed(false); }, [avatarUrl]);

  const handleAvatarUpload = async (file: File) => {
    if (!admin) return;
    setAvatarBusy(true);
    setAvatarError(null);
    try {
      const url = await uploadOwnAvatar(file);
      setAdminAvatarCache(admin.id, url);
    } catch {
      setAvatarError(tr('profil.avatar.error'));
    } finally {
      setAvatarBusy(false);
    }
  };

  const handleAvatarDelete = async () => {
    if (!admin) return;
    setAvatarBusy(true);
    setAvatarError(null);
    try {
      // Server-Antwort übernehmen: ein CRM-1.0-Bild bleibt ggf. sichtbar.
      setAdminAvatarCache(admin.id, await deleteOwnAvatar());
    } catch {
      setAvatarError(tr('profil.avatar.error'));
    } finally {
      setAvatarBusy(false);
    }
  };

  const displayName = `${form.firstName} ${form.lastName}`.trim() || form.email || '—';
  const initials = initialsFor(displayName, form.email);

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: `${d.rowY}px 0`, borderBottom: `1px solid ${t.borderSoft}`, alignItems: 'center', gap: 12 }}>
      <span style={{ fontSize: 12, color: t.muted, fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 13, color: t.heading, fontWeight: 500, textAlign: 'right' }}>{children}</span>
    </div>
  );

  const since = admin?.created_at ? new Date(admin.created_at).toLocaleDateString(locale, { month: 'long', year: 'numeric' }) : '—';

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : 'minmax(280px, 320px) 1fr',
      gap: isMobile ? 14 : d.gap, maxWidth: 1100,
    }}>
      {/* Left column */}
      <Card dark={dark} style={{ padding: d.cardPad }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', paddingBottom: 16, borderBottom: `1px solid ${t.borderSoft}` }}>
          <div style={{
            width: 96, height: 96, borderRadius: '50%', background: BRAND, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 34, fontWeight: 700, marginBottom: 10, overflow: 'hidden',
            boxShadow: '0 4px 10px rgba(181,41,122,0.25)',
          }}>
            {avatarUrl && !avatarFailed
              ? <img data-testid="profil-avatar-img" src={avatarUrl} alt={displayName} onError={() => setAvatarFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : initials}
          </div>
          {/* Feedback 11.10.2026: eigenes Profilbild hochladen / entfernen. */}
          <input
            ref={avatarInput}
            type="file"
            accept="image/*"
            data-testid="profil-avatar-input"
            style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void handleAvatarUpload(f); }}
          />
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={() => avatarInput.current?.click()}
              disabled={!admin || avatarBusy}
              style={{ background: 'none', border: `1px solid ${t.border}`, borderRadius: 8, padding: '5px 10px', fontSize: 12, color: t.text, cursor: avatarBusy ? 'wait' : 'pointer', fontFamily: 'Outfit, sans-serif' }}
            >
              {avatarBusy ? tr('profil.avatar.uploading') : avatarUrl ? tr('profil.avatar.change') : tr('profil.avatar.upload')}
            </button>
            {avatarUrl && (
              <button
                type="button"
                onClick={() => void handleAvatarDelete()}
                disabled={avatarBusy}
                style={{ background: 'none', border: 'none', padding: '5px 6px', fontSize: 12, color: t.muted, cursor: 'pointer', fontFamily: 'Outfit, sans-serif', textDecoration: 'underline' }}
              >
                {tr('profil.avatar.remove')}
              </button>
            )}
          </div>
          {avatarError && <div role="alert" style={{ fontSize: 12, color: '#b42318', marginBottom: 8 }}>{avatarError}</div>}
          <div style={{ fontSize: 16, fontWeight: 600, color: t.heading }}>{displayName}</div>
          <div style={{ fontSize: 13, color: t.muted, marginTop: 2 }}>{form.role || roleLabel(admin?.role, tr)}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
            <Badge tone="success" dark={dark}>{tr('profil.status.online')}</Badge>
            <Badge tone="brand" dark={dark}>{roleLabel(admin?.role, tr)}</Badge>
          </div>
        </div>
        <div style={{ paddingTop: 12 }}>
          <Row label={tr('profil.row.email')}>{form.email || '—'}</Row>
          <Row label={tr('profil.row.phone')}>{form.phone || '—'}</Row>
          <Row label={tr('profil.row.account_id')}>#{admin?.id ?? '…'}</Row>
          <Row label={tr('profil.row.since')}>{since}</Row>
        </div>
        <button
          onClick={handleEditOrSave}
          disabled={!admin || saving}
          style={{
            width: '100%', marginTop: 14,
            background: editing ? BRAND : t.card,
            color: editing ? '#fff' : t.text,
            border: editing ? 'none' : `1px solid ${t.border}`,
            borderRadius: 8, padding: '9px', fontSize: 13, fontWeight: 500,
            cursor: (!admin || saving) ? 'not-allowed' : 'pointer',
            opacity: (!admin || saving) ? 0.6 : 1,
            fontFamily: 'Outfit, sans-serif',
          }}
        >
          {saving ? tr('profil.action.saving') : editing ? tr('profil.action.save') : tr('profil.action.edit')}
        </button>
        {error && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#b42318', textAlign: 'center' }}>
            {error}
          </div>
        )}
      </Card>

      {/* Right column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap }}>
        {/* Personal info */}
        <Card dark={dark} style={{ padding: d.cardPad }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: t.heading, marginBottom: 16 }}>{tr('profil.section.personal_info.title')}</div>
          {!admin && !error && (
            <div style={{ fontSize: 12, color: t.muted, padding: '6px 0 14px' }}>{tr('profil.loading')}</div>
          )}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: 14,
          }}>
            <Field label={tr('profil.field.first_name')}>
              <Input value={form.firstName} onChange={setField('firstName')} disabled={!editing || !admin} dark={dark} style={{ opacity: editing ? 1 : 0.7 }}/>
            </Field>
            <Field label={tr('profil.field.last_name')}>
              <Input value={form.lastName} onChange={setField('lastName')} disabled={!editing || !admin} dark={dark} style={{ opacity: editing ? 1 : 0.7 }}/>
            </Field>
            <Field label={tr('profil.field.email')}>
              <Input value={form.email} onChange={setField('email')} disabled={!editing || !admin} dark={dark} style={{ opacity: editing ? 1 : 0.7 }}/>
            </Field>
            <Field label={tr('profil.field.phone')}>
              <Input value={form.phone} onChange={setField('phone')} disabled={!editing || !admin} dark={dark} style={{ opacity: editing ? 1 : 0.7 }}/>
            </Field>
            <Field label={tr('profil.field.role')}>
              <Input value={form.role} onChange={() => {/* read-only, derived from admin.role */}} disabled={true} dark={dark} style={{ opacity: 0.7 }}/>
            </Field>
          </div>
        </Card>

        {/* Telefonie / 3CX-Extension (feature/crm2-3cx-integration) */}
        <Card dark={dark} style={{ padding: d.cardPad }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.heading }}>{tr('profil.section.telephony.title')}</div>
              <div style={{ fontSize: 11.5, color: t.muted, marginTop: 2 }}>{tr('profil.section.telephony.hint')}</div>
            </div>
            {threeCxExtension && <Badge tone="success" dark={dark}>{tr('profil.section.telephony.active')}</Badge>}
          </div>
          {/* Kein eigener Button — die Extension wird über "Profil
              bearbeiten"/"Speichern" mit editiert und gespeichert. */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
            <div style={{ flex: 1, maxWidth: 200 }}>
              <Field label={tr('profil.field.three_cx_extension')}>
                <Input
                  value={extensionValue}
                  onChange={e => setExtensionValue(e.target.value.replace(/[^\d]/g, ''))}
                  disabled={!editing || saving}
                  placeholder={tr('profil.field.three_cx_extension_ph')}
                  dark={dark}
                  style={{ opacity: editing ? 1 : 0.7 }}
                />
              </Field>
            </div>
          </div>
          {extensionError && (
            <div style={{ marginTop: 10, fontSize: 12, color: '#b42318' }}>{extensionError}</div>
          )}
        </Card>

        {/* Security */}
        <Card dark={dark} style={{ padding: d.cardPad }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: t.heading }}>{tr('profil.section.security.title')}</div>
            <Badge tone="success" dark={dark}>{tr('profil.twofa_active')}</Badge>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <SecurityRow
              dark={dark}
              label={tr('profil.security.change_password')}
              hint={tr('profil.security.change_password_hint')}
              action={tr('profil.action.soon')}
              disabled={true}
            />
            <SecurityRow
              dark={dark}
              label={tr('profil.security.sessions')}
              hint={tr('profil.security.sessions_hint')}
              action={tr('profil.action.soon')}
              disabled={true}
            />
            <SecurityRow
              dark={dark}
              label={tr('profil.security.twofa')}
              hint={tr('profil.security.twofa_hint')}
              action={tr('profil.action.settings')}
              onClick={() => setPage?.('einstellungen')}
            />
          </div>
        </Card>
      </div>
    </div>
  );
}

interface SecurityRowProps {
  dark: boolean;
  label: string;
  hint: string;
  action: string;
  onClick?: () => void;
  disabled?: boolean;
}

function SecurityRow({ dark, label, hint, action, onClick, disabled }: SecurityRowProps) {
  const t = getThemeColors(dark);
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
      <div>
        <div style={{ fontSize: 13, color: t.heading, fontWeight: 500 }}>{label}</div>
        <div style={{ fontSize: 11, color: t.muted, marginTop: 2 }}>{hint}</div>
      </div>
      <Button variant="secondary" size="sm" dark={dark} disabled={disabled} onClick={onClick}>{action}</Button>
    </div>
  );
}
